import React, { useEffect, useMemo } from "react";
import { api } from "@/lib/api";
import VisualizerCore from "@/components/VisualizerCore";

/**
 * The retailer's own direct visualizer — reached via the "Visualize" button
 * on the dashboard. Same core experience as the salesperson's /sales page,
 * but scoped to the retailer's own account (no salesperson login needed,
 * since they're already authenticated as the retailer).
 */
export default function RetailerVisualizer({ embedded = false }) {
  const visualizerApi = useMemo(
    () => ({
      getRetailer: () => api.get("/auth/me").then((r) => r.data),
      getProducts: (category) => api.get("/products", { params: { category } }).then((r) => r.data),
      uploadFile: (file) => {
        const fd = new FormData();
        fd.append("file", file);
        return api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
      },
      submitRender: (payload) => api.post("/history", payload).then((r) => r.data),
      getRenderStatus: (id) => api.get(`/history/${id}`).then((r) => r.data),
    }),
    []
  );

  useEffect(() => {
    ["viz_history", "viz_historyIndex", "viz_sceneState", "viz_historyMeta"].forEach((k) => sessionStorage.removeItem(k));
  }, []);

  // Hide the global Tawk.to chat widget while the visualizer is open — it
  // covers canvas controls on small screens. Restored on unmount so it still
  // shows on every other page.
  useEffect(() => {
    const hide = () => window.Tawk_API?.hideWidget?.();
    const show = () => window.Tawk_API?.showWidget?.();

    hide();
    const prevOnLoad = window.Tawk_API?.onLoad;
    if (window.Tawk_API) {
      window.Tawk_API.onLoad = function () {
        prevOnLoad?.();
        hide();
      };
    }
    // Widget script loads asynchronously — keep retrying briefly until it's ready.
    const interval = setInterval(() => {
      if (window.Tawk_API?.hideWidget) {
        hide();
        clearInterval(interval);
      }
    }, 300);

    return () => {
      clearInterval(interval);
      if (window.Tawk_API) window.Tawk_API.onLoad = prevOnLoad;
      show();
    };
  }, []);

  return <VisualizerCore api={visualizerApi} notFoundMessage="Could not load your store." embedded={embedded} />;
}
